import { useMemo } from 'react';
import { cva } from 'class-variance-authority';
import { Flex, Text, Button, Skeleton } from '@nula/core-ui';

import { SidePanel } from '@/components/side-panel/side-panel';

import { useEntityAttendees } from '@/lib/recordings/use-entity-attendees';
import { AttendeeRow } from './attendee-row';
import { AddAttendeeInput } from './add-attendee-input';
import styles from './attendees-side-panel.module.css';

export type AttendeesSidePanelProps = {
  entityId: string;
  /** The surface the panel is scoped to. Omitting it (or null) falls back to
   * the document-level roster. */
  surfaceId?: string | null;
  onClose: () => void;
};

const body = cva(styles.body);
const rowList = cva(styles.rowList);

/**
 * Right-edge overlay surface listing attendees for a meeting entity.
 * Mirrors `TranscriptSidePanel` visually. Calendar-sourced attendees
 * render before manual ones; both sorts stable within their group.
 *
 * M4: collapsed rows only.
 * M5: full `AttendeeRow` with expand/collapse + "X" + `AddAttendeeInput` footer.
 */
export function AttendeesSidePanel({ entityId, surfaceId = null, onClose }: AttendeesSidePanelProps) {
  const { attendees, loading, error, refetch, addAttendee, removeAttendee } =
    useEntityAttendees(entityId, surfaceId);
  // Preserve the prior nullable-`data` semantics: null while loading (→ skeleton),
  // the array once loaded.
  const data = loading ? null : attendees;

  // Calendar first, then manual; stable order within each group.
  const ordered = useMemo(() => {
    if (!data) return [];
    const calendar = data.filter((a) => a.source === 'calendar');
    const manual = data.filter((a) => a.source === 'manual');
    return [...calendar, ...manual];
  }, [data]);

  const count = ordered.length;

  const titleExtra = data ? (
    <Text size="xs" className={styles.count} data-testid="attendees-count">{count}</Text>
  ) : null;

  const footer = !loading && !error ? (
    <AddAttendeeInput onAdd={(email) => addAttendee(email)} />
  ) : undefined;

  return (
    <SidePanel
      title="Attendees"
      icon="group"
      onClose={onClose}
      titleExtra={titleExtra}
      bodyClassName={body()}
      footer={footer}
    >
      <div
        data-testid="attendees-busy-region"
        aria-busy={loading}
        aria-live="polite"
      >
        {loading && (
          <Flex vertical gap="md" className={styles.skeletonContainer} data-testid="attendees-skeleton">
            <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 1, width: '60%' }} />
            <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 1, width: '60%' }} />
            <Skeleton active title={{ width: '80%' }} paragraph={{ rows: 1, width: '60%' }} />
          </Flex>
        )}

        {!loading && error && (
          <Flex vertical gap="md" align="center" className={styles.emptyState}>
            <Text>Couldn't load attendees</Text>
            <Button onClick={() => void refetch()}>Retry</Button>
          </Flex>
        )}

        {!loading && !error && ordered.length === 0 && (
          <Flex vertical gap="sm" align="center" className={styles.emptyState}>
            <Text>No attendees yet</Text>
          </Flex>
        )}

        {!loading && !error && ordered.length > 0 && (
          <Flex vertical className={rowList()}>
            {ordered.map((a) => (
              <AttendeeRow
                key={`${a.source}:${a.email}`}
                attendee={a}
                onRemove={(email) => {
                  void removeAttendee(email).catch(() => {
                    // Hook already rolled back optimistically;
                    // error surface is a future toast — no-op here.
                  });
                }}
              />
            ))}
          </Flex>
        )}
      </div>
    </SidePanel>
  );
}
